import { useRef, useState, useEffect } from "react";
import {
  EmailShareButton, EmailIcon,
  FacebookShareButton, FacebookIcon,
  TwitterShareButton,
  WhatsappShareButton, WhatsappIcon,
  LinkedinShareButton, LinkedinIcon,
} from "react-share";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import XTwitterIcon from '../../common/XTwitterIcon';
import ShareQrCode from './ShareQrCode';
import EmbedWidgetPicker from './EmbedWidgetPicker';

const XIcon = () => (
  <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <XTwitterIcon fill="white" width={22} height={22} />
  </div>
);

const GAP = 15;
const EDGE_PADDING = 16;
const MIN_HEIGHT = 200;

const SocialShareModal = ({ isOpen, shareUrl, shareTitle, closeModal, heading, epkTitle }) => {
  const [placement, setPlacement] = useState({ side: "top", maxHeight: 400 });
  const modalRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Measure the anchor (a normal-flow parent, unaffected by this popover's own
      // absolute positioning) rather than the popover itself - the popover grew with
      // the QR/embed sections and can now exceed the room on either side, so pick
      // whichever side has more space and cap height to what's actually available
      // instead of a flat 85vh that can render past the viewport edge.
      const anchor = modalRef.current.parentElement.getBoundingClientRect();
      const spaceBelow = window.innerHeight - anchor.bottom - GAP - EDGE_PADDING;
      const spaceAbove = anchor.top - GAP - EDGE_PADDING;
      const side = spaceBelow >= spaceAbove ? "bottom" : "top";
      const maxHeight = Math.max(MIN_HEIGHT, side === "bottom" ? spaceBelow : spaceAbove);
      setPlacement({ side, maxHeight });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl)
      .then(() => toast.success(t("Link copied to clipboard!")))
      .catch(() => toast.error(t("Could not copy link")));
  };

  return (
    <>
      <div
        className="tw-fixed tw-inset-0 tw-z-[9998]"
        onClick={closeModal}
        aria-hidden="true"
      />
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        className="tw-absolute tw-z-[9999] tw-w-[290px] md:tw-w-[380px] tw-overflow-y-auto tw-rounded-[13px] tw-shadow-2xl tw-p-4 tw-border tw-border-white/20 tw-transition-opacity tw-duration-300 tw-opacity-100"
        style={{
          right: 0,
          maxHeight: placement.maxHeight,
          ...(placement.side === "top"
            ? { bottom: "calc(100% + 15px)" }
            : { top: "calc(100% + 15px)" }),
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
      >
        <div className="tw-flex tw-justify-between tw-items-start tw-mb-4">
          <h5 className="tw-font-bold tw-text-lg tw-text-[#1e0039] tw-w-full tw-text-center tw-m-0">
            {heading || t('Share your EPK with the world!')}
          </h5>
          <button
            type="button"
            onClick={closeModal}
            className="tw-text-2xl tw-leading-none tw-text-gray-500 hover:tw-text-[#e81a84] tw-bg-transparent tw-border-none tw-cursor-pointer tw-absolute tw-right-4 tw-top-3"
            aria-label={t("Close")}
          >
            &times;
          </button>
        </div>

        <div className="tw-flex tw-justify-between tw-mb-5">
          <FacebookShareButton url={shareUrl} quote={shareTitle} className="hover:tw-scale-110 tw-transition-transform">
            <FacebookIcon size={40} round />
          </FacebookShareButton>
          <WhatsappShareButton url={shareUrl} title={shareTitle} className="hover:tw-scale-110 tw-transition-transform">
            <WhatsappIcon size={40} round />
          </WhatsappShareButton>
          <TwitterShareButton url={shareUrl} title={shareTitle} className="hover:tw-scale-110 tw-transition-transform">
            <XIcon />
          </TwitterShareButton>
          <LinkedinShareButton url={shareUrl} title={shareTitle} className="hover:tw-scale-110 tw-transition-transform">
            <LinkedinIcon size={40} round />
          </LinkedinShareButton>
          <EmailShareButton url={shareUrl} subject={shareTitle} body={`Check it out: ${shareUrl}`} className="hover:tw-scale-110 tw-transition-transform">
            <EmailIcon size={40} round />
          </EmailShareButton>
        </div>

        <div className="tw-flex tw-w-full tw-bg-[#e6e2e2] tw-rounded tw-overflow-hidden">
          <input
            type="url"
            value={shareUrl}
            className="tw-flex-1 tw-bg-transparent tw-border-none tw-px-3 tw-py-2 tw-text-sm tw-text-gray-700 tw-outline-none"
            readOnly
          />
          <button
            onClick={copyToClipboard}
            className="tw-w-12 tw-flex tw-justify-center tw-items-center tw-bg-gray-300 hover:tw-bg-gray-400 tw-transition-colors tw-border-none tw-cursor-pointer"
          >
            <FontAwesomeIcon icon={faCopy} className="tw-text-gray-700" />
          </button>
        </div>

        <div className="tw-my-4 tw-h-px tw-bg-black/10" />
        <ShareQrCode value={shareUrl} />

        <div className="tw-my-4 tw-h-px tw-bg-black/10" />
        <EmbedWidgetPicker epkUrl={shareUrl} title={epkTitle} />
      </div>
    </>
  );
};

export default SocialShareModal;
